import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { modulesAPI } from '@/services';
import { useToast } from '../components/Toast';
import { usePermissions } from '../hooks/usePermissions';
import { useAuth } from '../contexts/AuthContext';
import { useModules } from '../contexts/ModulesContext';
import PageHeader from '../components/PageHeader';
import { ENABLE_EMBEDDED_SAAS_UI, ADMIN_CONSOLE_URL, adminConsoleUrl } from '../config/platform';
import { isSaasAdminRole } from '../utils/iamRoles';

const ModulesManagement = () => {
  const navigate = useNavigate();
  const { error, success } = useToast();
  const { hasPermission } = usePermissions();
  const { user } = useAuth();
  const { refreshModules } = useModules();
  const isSaasAdmin = isSaasAdminRole(user?.role);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});

  useEffect(() => {
    loadModules();
  }, []);

  const loadModules = async () => {
    try {
      setLoading(true);
      const response = await modulesAPI.getAll();
      setModules(response.data || []);
    } catch (err) {
      console.error('Failed to load modules:', err);
      error('Erreur lors du chargement des modules');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleModule = async (moduleId, enabled) => {
    try {
      setSaving({ ...saving, [moduleId]: true });
      await modulesAPI.updateModule(moduleId, { enabled });
      setModules(modules.map(m => m.id === moduleId ? { ...m, enabled } : m));
      // Rafraîchir le contexte pour que les changements soient immédiatement visibles
      refreshModules();
      success(`Module ${enabled ? 'activé' : 'désactivé'} avec succès`);
    } catch (err) {
      console.error('Failed to update module:', err);
      error(err.response?.data?.detail || 'Erreur lors de la mise à jour du module');
    } finally {
      setSaving({ ...saving, [moduleId]: false });
    }
  };

  const handleToggleComponent = async (moduleId, componentId, enabled) => {
    try {
      const savingKey = `${moduleId}-${componentId}`;
      setSaving({ ...saving, [savingKey]: true });
      await modulesAPI.updateComponent(moduleId, componentId, { enabled });
      setModules(modules.map(m => 
        m.id === moduleId 
          ? { 
              ...m, 
              components: m.components.map(c => 
                c.id === componentId ? { ...c, enabled } : c
              )
            }
          : m
      ));
      // Rafraîchir le contexte pour que les changements soient immédiatement visibles
      refreshModules();
      success(`Composant ${enabled ? 'activé' : 'désactivé'} avec succès`);
    } catch (err) {
      console.error('Failed to update component:', err);
      error(err.response?.data?.detail || 'Erreur lors de la mise à jour du composant');
    } finally {
      const savingKey = `${moduleId}-${componentId}`;
      setSaving({ ...saving, [savingKey]: false });
    }
  };

  const getIconComponent = (iconName) => {
    const iconMap = {
      'dollar-sign': (
        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      'calendar': (
        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
      'users': (
        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ),
      'file-text': (
        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      'clipboard-list': (
        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
        </svg>
      ),
      'bell': (
        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
      ),
      'settings': (
        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      'credit-card': (
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
        </svg>
      ),
      'arrow-left-right': (
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
        </svg>
      ),
      'calendar-days': (
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
      'user-check': (
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      'list': (
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      ),
      'user': (
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      ),
      'folder': (
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
        </svg>
      ),
      'file-check': (
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      'bell-ring': (
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
      ),
      'user-cog': (
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      'shield': (
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
      'cog': (
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      'puzzle': (
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" />
        </svg>
      )
    };
    return iconMap[iconName] || (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des modules...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-6 md:py-8">
        <PageHeader
          title="Applications & modules"
          subtitle={isSaasAdmin 
            ? "Pour gérer les modules par organisation, utilisez la page de détails de chaque organisation"
            : "Affichage des modules disponibles (consultation seule). La gestion des modules est effectuée par l'administrateur SaaS."
          }
        />

        {isSaasAdmin && (
          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="text-sm font-medium text-blue-900">
                  Gestion des modules par organisation
                </p>
                <p className="text-sm text-blue-700 mt-1">
                  Pour activer/désactiver les modules pour une organisation spécifique, allez dans la liste des organisations et cliquez sur une organisation pour accéder à l'onglet "Modules".
                </p>
                <button
                  type="button"
                  onClick={() => {
                    if (ENABLE_EMBEDDED_SAAS_UI) {
                      navigate('/saas/organizations');
                    } else if (ADMIN_CONSOLE_URL) {
                      window.open(adminConsoleUrl(), '_blank', 'noopener,noreferrer');
                    } else {
                      error('Définissez REACT_APP_ADMIN_CONSOLE_URL pour ouvrir la console plateforme.');
                    }
                  }}
                  className="mt-2 text-sm text-blue-600 hover:text-blue-800 font-medium underline"
                >
                  {ENABLE_EMBEDDED_SAAS_UI ? 'Voir les organisations →' : 'Ouvrir la console plateforme →'}
                </button>
              </div>
            </div>
          </div>
        )}

        {!isSaasAdmin && (
          <div className="mt-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-amber-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div>
                <p className="text-sm font-medium text-amber-900">
                  Mode consultation seule
                </p>
                <p className="text-sm text-amber-700 mt-1">
                  Vous pouvez consulter les modules disponibles, mais seule la modification est réservée à l'administrateur SaaS qui gère les modules par organisation.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-6 mt-8">
          {modules.map((module) => (
            <div key={module.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              {/* Module Header */}
              <div className={`p-6 border-b border-gray-200 ${module.enabled ? 'bg-gradient-to-r from-indigo-50 to-purple-50' : 'bg-gray-50'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`p-3 rounded-lg ${module.enabled ? 'bg-indigo-100 text-indigo-600' : 'bg-gray-200 text-gray-400'}`}>
                      {getIconComponent(module.icon)}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">{module.display_name}</h3>
                      {module.description && (
                        <p className="text-sm text-gray-600 mt-1">{module.description}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {isSaasAdmin ? (
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={module.enabled}
                          onChange={(e) => handleToggleModule(module.id, e.target.checked)}
                          disabled={saving[module.id]}
                          className="sr-only peer"
                        />
                        <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-indigo-600"></div>
                        <span className="ml-3 text-sm font-medium text-gray-700">
                          {module.enabled ? 'Activé' : 'Désactivé'}
                        </span>
                      </label>
                    ) : (
                      <span className={`px-3 py-1 text-sm font-medium rounded-full ${
                        module.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {module.enabled ? 'Activé' : 'Désactivé'}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Components */}
              {module.enabled && module.components && module.components.length > 0 && (
                <div className="p-6">
                  <h4 className="text-sm font-semibold text-gray-700 mb-4 uppercase tracking-wide">Composants</h4>
                  <div className="space-y-3">
                    {module.components.map((component) => (
                      <div key={component.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${component.enabled ? 'bg-green-100 text-green-600' : 'bg-gray-200 text-gray-400'}`}>
                            {getIconComponent(component.icon)}
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">{component.display_name}</p>
                            {component.description && (
                              <p className="text-xs text-gray-500 mt-1">{component.description}</p>
                            )}
                            {component.route && (
                              <p className="text-xs text-indigo-600 mt-1">{component.route}</p>
                            )}
                          </div>
                        </div>
                        {isSaasAdmin ? (
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={component.enabled}
                              onChange={(e) => handleToggleComponent(module.id, component.id, e.target.checked)}
                              disabled={saving[`${module.id}-${component.id}`] || !module.enabled}
                              className="sr-only peer"
                            />
                            <div className={`w-11 h-6 rounded-full peer ${
                              !module.enabled 
                                ? 'bg-gray-300 cursor-not-allowed' 
                                : 'bg-gray-200 peer-checked:bg-green-600'
                            } peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all`}></div>
                          </label>
                        ) : (
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            component.enabled && module.enabled
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {component.enabled && module.enabled ? 'Activé' : 'Désactivé'}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ModulesManagement;

